import { useIntl } from 'react-intl';
import { Box, Chip, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment';
import { ORDER_STATUSES, orderStatusLabel } from './readiness';
import {
  OrderReadiness,
  ReadinessCheck,
  ReadinessMetrics,
  ReadinessOrderStatus,
} from './types';
import * as Styles from './Readiness.css';

const VERDICT_SEVERITY = {
  GO: 'success',
  RISK: 'warning',
  NO_GO: 'error',
} as const;

const STATUS_METRIC: Record<ReadinessOrderStatus, keyof ReadinessMetrics> = {
  ACCEPTED: 'accepted',
  PENDING: 'pending',
  NONE: 'none',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
};

const KNOWN_CHECKS = [
  'COVERAGE',
  'REPORTING_WITHOUT_ORDER',
  'PENDING_AFTER_DATE',
  'NEEDS_CORRECTION',
];

const CheckCard = ({ check }: { check: ReadinessCheck }) => {
  const intl = useIntl();
  const percent = check.key === 'COVERAGE';
  const number = (value: number) =>
    `${intl.formatNumber(value, { maximumFractionDigits: 1 })}${
      percent ? '%' : ''
    }`;
  const color = Styles.CHECK_COLORS[check.status];
  let target = '';
  if (check.threshold !== null) {
    target = intl.formatMessage(
      {
        id:
          percent && check.warnThreshold !== null
            ? 'ORDER_REPORTING.TARGET_MIN_WARN'
            : `ORDER_REPORTING.TARGET_${percent ? 'MIN' : 'MAX'}`,
      },
      {
        threshold: number(check.threshold),
        warn: check.warnThreshold === null ? '' : number(check.warnThreshold),
      },
    );
  }
  const label = KNOWN_CHECKS.includes(check.key)
    ? intl.formatMessage({ id: `ORDER_REPORTING.CHECK.${check.key}` })
    : check.key;

  return (
    <Styles.CheckCard variant="outlined" $color={color}>
      <Box display="flex" justifyContent="space-between" gridGap={8}>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
        <Styles.Pill $color={color}>
          {intl.formatMessage({
            id: `ORDER_REPORTING.CHECK_STATUS.${check.status}`,
          })}
        </Styles.Pill>
      </Box>
      <Box display="flex" alignItems="baseline" gridGap={8}>
        <Styles.CheckValue $color={color}>
          {number(check.value)}
        </Styles.CheckValue>
        <Typography variant="caption" color="textSecondary">
          {target}
        </Typography>
      </Box>
    </Styles.CheckCard>
  );
};

interface Props {
  data: OrderReadiness;
  status: ReadinessOrderStatus | '';
  onStatusChange: (status: ReadinessOrderStatus | '') => void;
}

// Verdict, go/no-go checks and the order status breakdown of the population.
const ReadinessSummary = ({ data, status, onStatusChange }: Props) => {
  const intl = useIntl();
  const { metrics, checks, verdict } = data;
  const date = data.date ? moment(data.date).format('DD/MM/YYYY') : '—';
  const count = (value: ReadinessCheck['status']) =>
    checks.filter((check) => check.status === value).length;

  return (
    <>
      {verdict ? (
        <Alert severity={VERDICT_SEVERITY[verdict]} variant="filled">
          <strong>
            {intl.formatMessage({ id: `ORDER_REPORTING.VERDICT.${verdict}` })}
          </strong>
          {' — '}
          <span>
            {intl.formatMessage(
              { id: `ORDER_REPORTING.VERDICT_TEXT.${verdict}` },
              { date, count: count(verdict === 'RISK' ? 'WARN' : 'FAIL') },
            )}
          </span>
        </Alert>
      ) : (
        <Alert severity="info">
          {intl.formatMessage({ id: 'ORDER_REPORTING.VERDICT_TEXT.UNKNOWN' })}
        </Alert>
      )}

      {checks.length > 0 && (
        <Styles.CheckGrid>
          {checks.map((check) => (
            <CheckCard key={check.key} check={check} />
          ))}
        </Styles.CheckGrid>
      )}

      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        gridGap={8}
        mt={checks.length ? 0 : 1.5}
        mb={1.5}
      >
        {ORDER_STATUSES.map((value) => {
          const active = status === value;
          return (
            <Chip
              key={value}
              size="small"
              clickable
              icon={<Styles.Dot $color={Styles.ORDER_STATUS_COLORS[value]} />}
              label={`${orderStatusLabel(intl, value)} ${
                metrics[STATUS_METRIC[value]]
              }`}
              color={active ? 'primary' : 'default'}
              variant={active ? 'default' : 'outlined'}
              aria-pressed={active}
              onClick={() => onStatusChange(active ? '' : value)}
            />
          );
        })}
        <Typography variant="body2" color="textSecondary">
          {intl.formatMessage(
            { id: 'ORDER_REPORTING.SUMMARY' },
            {
              pairs: metrics.pairs,
              reporting: metrics.reportingPairs,
              hours: intl.formatNumber(metrics.hoursWithoutOrder, {
                maximumFractionDigits: 2,
              }),
            },
          )}
        </Typography>
      </Box>
    </>
  );
};

export default ReadinessSummary;
